import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { OrderTotalValueContainer } from '@container/OrderTotalValueContainer';
import { PartsNaviContainer } from '@container/PartsNaviContainer';
import { CartInDialogContainer, CartInErrorDialogContainer } from '@container/Dialog';
import { Order3dContainer, OrderPartsImg } from '@root/app/react/uikit';
import { r2o } from '@app/react/common/route2orderparts';
import { pageOpenAction } from '@app/react/action/PageOpenDetectionAction';
import { showOrderPartsHelpAction } from '@app/react/action/showOrderPartsHelp';
import { ROUTE_PARTS, ORDER_PARTS_NAME } from '@app/react/const/variables';
import { orderPartsHeaderInfos } from '@app/react/const/orderPartsHeaderInfos';
import type { OrderPathParams } from '@app/react/const/type';
import { FooterNavigationBarContainer } from './FooterNavigationBarContainer';

const orderPartsHeaderNames = {
    ...ORDER_PARTS_NAME.ja,
    preview: 'カスタマイズ',
};

interface OrderPathParamsWithPartsName extends OrderPathParams {
    partsName: string;
}

export const MobileRender: FC<RouteComponentProps<OrderPathParamsWithPartsName>> = ({
    match,
}) => {
    const { partsName } = match.params;
    const pName = r2o(partsName);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(pageOpenAction({
            partsName: (pName === 'preview' ? null : pName) ?? null,
        }));
    }, [dispatch, pName]);

    const handleHelpClick = useCallback(() => {
        if (pName === 'preview') {
            return;
        }
        dispatch(showOrderPartsHelpAction({ partsName: pName }));
    }, [dispatch, pName]);

    const { ok, visible, epm } = useMemo(() => ({
        ok: [...ROUTE_PARTS, 'preview'].some((x) => x === partsName),
        visible: !(pName === 'familycrest' || pName === 'carving'),
        epm: pName === 'preview',
    }), [partsName, pName]);

    if (pName === null || pName === undefined) {
        // home view
        return (
            <>
                <OrderPartsImg partsName="HOME" onHelpClick={() => false} />
                <CartInDialogContainer />
                <CartInErrorDialogContainer />
            </>
        );
    }

    if (!ok) {
        return null;
    }

    return (
        <>
            <OrderPartsImg
                partsName={orderPartsHeaderNames[pName]}
                className="preview-3d-header"
                showHelpBtn={orderPartsHeaderInfos[pName].showHelpBtn}
                onHelpClick={handleHelpClick}
            />
            <OrderTotalValueContainer />
            <Order3dContainer />
            <PartsNaviContainer visible={visible} enablePreviewMode={epm} />
            <FooterNavigationBarContainer enablePreview={epm} />
            <CartInDialogContainer />
            <CartInErrorDialogContainer />
        </>
    );
};

export const DesktopRender: FC<RouteComponentProps<OrderPathParamsWithPartsName>> = ({
    match,
}) => {
    const { partsName } = match.params;
    const pName = r2o(partsName);

    const dispatch = useDispatch();

    const isCustomView = useMemo(() => [...ROUTE_PARTS].some((x) => x === partsName), [partsName]);

    useEffect(() => {
        dispatch(pageOpenAction({
            partsName: (pName === 'preview' ? null : pName) ?? null,
        }));
    }, [dispatch, pName]);

    return (
        <>
            <OrderTotalValueContainer />
            <Order3dContainer />
            <PartsNaviContainer visible enablePreviewMode={false} />
            <FooterNavigationBarContainer isCustomView={isCustomView} />
            <CartInDialogContainer />
            <CartInErrorDialogContainer />
        </>
    );
};
